import React, { useState, useMemo } from "react";
import { format, setHours, setMinutes, isBefore, differenceInHours, addDays, nextSaturday } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarIcon, Clock, Globe, Sparkles, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CreateExhibitionEventModalProps {
  onEventCreated?: () => void;
  trigger?: React.ReactNode;
}

// Generate time options in 30-minute increments
const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = (i % 2) * 30;
  const value = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  const label = format(setMinutes(setHours(new Date(), hours), minutes), "h:mm a");
  return { value, label };
});

const TIMEZONE = "America/New_York";

const CreateExhibitionEventModal: React.FC<CreateExhibitionEventModalProps> = ({
  onEventCreated,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<"default" | "custom">("default");
  const [validationError, setValidationError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("23:00");

  // Calculate the next Saturday for default schedule
  const defaultSaturday = useMemo(() => {
    const now = new Date();
    return nextSaturday(now);
  }, []);

  // Build summary text
  const summaryText = useMemo(() => {
    if (scheduleMode === "default") {
      return `${format(defaultSaturday, "EEEE, MMM d")} · 12:00 PM – 11:00 PM (EST)`;
    }

    if (!customDate || !startTime || !endTime) {
      return null;
    }

    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);
    const startLabel = format(setMinutes(setHours(new Date(), startH), startM), "h:mm a");
    const endLabel = format(setMinutes(setHours(new Date(), endH), endM), "h:mm a");

    return `${format(customDate, "EEEE, MMM d")} · ${startLabel} – ${endLabel} (EST)`;
  }, [scheduleMode, defaultSaturday, customDate, startTime, endTime]);

  const validateCustomSchedule = (): boolean => {
    if (!customDate) {
      setValidationError("Please select a valid date and time");
      return false;
    }

    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);

    const startDateTime = setMinutes(setHours(customDate, startH), startM);
    const endDateTime = setMinutes(setHours(customDate, endH), endM);

    if (!isBefore(startDateTime, endDateTime)) {
      setValidationError("End time must be after start time");
      return false;
    }

    const duration = differenceInHours(endDateTime, startDateTime);
    if (duration > 24) {
      setValidationError("Event duration cannot exceed 24 hours");
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    if (scheduleMode === "custom" && !validateCustomSchedule()) {
      return;
    }

    setIsLoading(true);

    try {
      let startsAt: string;
      let endsAt: string;

      if (scheduleMode === "default") {
        // Default: next Saturday 12:00 PM - 11:00 PM EST
        // The RPC handles default scheduling when nulls are passed
        const { data, error } = await supabase.rpc("create_exhibition_event", {
          p_name: name.trim(),
          p_description: description.trim() || null,
          p_starts_at: null,
          p_ends_at: null,
        });

        if (error) throw error;
        
        console.log("[CreateExhibitionEvent] Created with default schedule:", data);
      } else {
        // Custom schedule - build timestamps with timezone offset
        const [startH, startM] = startTime.split(":").map(Number);
        const [endH, endM] = endTime.split(":").map(Number);

        const startDateTime = setMinutes(setHours(customDate!, startH), startM);
        const endDateTime = setMinutes(setHours(customDate!, endH), endM);

        // Format as ISO with EST offset (-05:00 or -04:00 for EDT)
        // We'll pass the local time and let the backend handle UTC conversion
        startsAt = formatInTimeZone(startDateTime, TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");
        endsAt = formatInTimeZone(endDateTime, TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX");

        console.log("[CreateExhibitionEvent] Custom schedule:", { startsAt, endsAt });

        const { data, error } = await supabase.rpc("create_exhibition_event", {
          p_name: name.trim(),
          p_description: description.trim() || null,
          p_starts_at: startsAt,
          p_ends_at: endsAt,
        });

        if (error) throw error;
        
        console.log("[CreateExhibitionEvent] Created:", data);
      }

      toast.success("Event created successfully!");
      setIsOpen(false);
      resetForm();
      onEventCreated?.();
    } catch (error: any) {
      console.error("[CreateExhibitionEvent] Error:", error);
      toast.error(error.message || "Failed to create event");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setScheduleMode("default");
    setCustomDate(undefined);
    setStartTime("12:00");
    setEndTime("23:00");
    setValidationError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) resetForm();
    }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gap-2">
            <Sparkles className="w-4 h-4" />
            Create Event
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Create Exhibition Event
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Event Name */}
          <div className="space-y-2">
            <Label htmlFor="event-name">Event Name *</Label>
            <Input
              id="event-name"
              placeholder="Valentine's Love Exhibition"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="event-description">Description</Label>
            <Textarea
              id="event-description"
              placeholder="Special event for Valentine's Day..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          {/* Schedule Mode Toggle */}
          <div className="space-y-3">
            <Label>Event Schedule</Label>
            <RadioGroup
              value={scheduleMode}
              onValueChange={(val) => {
                setScheduleMode(val as "default" | "custom");
                setValidationError(null);
              }}
              className="space-y-3"
            >
              <div
                className={cn(
                  "flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                  scheduleMode === "default"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
                onClick={() => setScheduleMode("default")}
              >
                <RadioGroupItem value="default" id="default" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="default" className="font-semibold cursor-pointer">
                    Default Schedule
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Next Saturday · 12:00 PM – 11:00 PM (EST)
                  </p>
                </div>
              </div>

              <div
                className={cn(
                  "flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                  scheduleMode === "custom"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
                onClick={() => setScheduleMode("custom")}
              >
                <RadioGroupItem value="custom" id="custom" className="mt-0.5" />
                <div className="flex-1">
                  <Label htmlFor="custom" className="font-semibold cursor-pointer">
                    Custom Schedule
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose any date and time
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Custom Schedule Fields */}
          {scheduleMode === "custom" && (
            <div className="space-y-4 p-4 rounded-xl bg-muted/30 border border-muted">
              {/* Event Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  Event Date
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !customDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDate ? format(customDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDate}
                      onSelect={setCustomDate}
                      disabled={(date) => date < addDays(new Date(), -1)}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  This is the day the exhibition will run.
                </p>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  Start Time
                </Label>
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select start time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  When the exhibition opens to users.
                </p>
              </div>

              {/* End Time */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  End Time
                </Label>
                <Select value={endTime} onValueChange={setEndTime}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select end time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  When the exhibition closes automatically.
                </p>
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  Timezone
                </Label>
                <div className="px-3 py-2 rounded-md bg-muted/50 border text-sm">
                  America/New_York (EST/EDT)
                </div>
                <p className="text-xs text-muted-foreground">
                  All users will see times converted to their local timezone.
                </p>
                <p className="text-xs text-muted-foreground/70 italic">
                  Stored internally in UTC.
                </p>
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {validationError}
                </div>
              )}
            </div>
          )}

          {/* Summary Text */}
          {summaryText && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
              <p className="text-sm text-muted-foreground">Event will run:</p>
              <p className="font-semibold text-foreground">{summaryText}</p>
            </div>
          )}

          {/* Admin Safety Hint */}
          <p className="text-xs text-muted-foreground text-center">
            Only admins can create custom schedules.
            <br />
            Event state (Live / Locked / Closed) is calculated automatically.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              setIsOpen(false);
              resetForm();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            disabled={isLoading || !name.trim()}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
                Creating Event…
              </>
            ) : (
              "Create Event"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExhibitionEventModal;
